import React from 'react';
import { Download, Search } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { downloadExport, monthLabel } from './posUtils';

export const Kpi = ({ label, value, sub, tone = 'text-slate-900', onClick, testid }) => (
  <Card onClick={onClick}
    className={`p-4 transition-shadow duration-200 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-emerald-300' : ''}`}
    data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className={`text-2xl font-bold mt-1 tabular-nums ${tone}`} data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </Card>
);

export const Section = ({ title, right, children, testid }) => (
  <Card className="p-4 min-w-0" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h3 className="font-semibold text-slate-800 text-sm sm:text-base">{title}</h3>
      {right}
    </div>
    {children}
  </Card>
);

export const ExportBtn = ({ section, params, testid }) => (
  <Button size="sm" variant="outline" onClick={() => downloadExport(section, params)} data-testid={testid || `export-${section}-btn`}>
    <Download className="w-4 h-4 mr-1" /> Export Excel
  </Button>
);

export const SearchBox = ({ value, onChange, testid, placeholder = 'Search ID, name or mobile' }) => (
  <div className="relative">
    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="pl-8 h-9 w-full sm:w-56" data-testid={testid} />
  </div>
);

export const PeriodNote = ({ data }) => {
  if (!data || data.empty) return null;
  const single = data.from_month === data.to_month;
  return (
    <div className="text-xs text-slate-500" data-testid="period-note">
      Starting period <b className="text-slate-700">{monthLabel(data.start_month)}</b> → ending period{' '}
      <b className="text-slate-700">{monthLabel(data.end_month)}</b>
      {single ? ' (compared with previous month)' : ''}
      {data.missing_months?.length > 0 && (
        <span className="ml-2 text-amber-700">No data imported for: {data.missing_months.map(monthLabel).join(', ')}</span>
      )}
    </div>
  );
};

export const EmptyState = () => (
  <Card className="p-10 text-center text-slate-500" data-testid="pos-empty-state">
    No POS data imported yet. Use <b>Import Excel</b> to upload a monthly customer sales file.
  </Card>
);

export const StatusBadge = ({ s }) => {
  const tone = { New: 'bg-sky-100 text-sky-800', Active: 'bg-emerald-100 text-emerald-800', Inactive: 'bg-amber-100 text-amber-800', Lost: 'bg-red-100 text-red-700' }[s] || 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone}`}>{s}</span>;
};

export const Delta = ({ v }) => {
  if (v == null) return <span className="text-slate-400">—</span>;
  return <span className={v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-600' : 'text-slate-500'}>{v > 0 ? '+' : ''}{Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>;
};
